import React from 'react';
import {motion} from 'framer-motion';
import {FiShield,FiTrendingUp,FiCheckCircle,FiArrowRight,FiAward,FiUsers} from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';
import SafeIcon from '../common/SafeIcon';
import {Link} from 'react-router-dom';

const {FaStar} = FaIcons;

const stats=[
  {icon: FiAward,value: '15+',label: 'Years of Excellence'},
  {icon: FiUsers,value: '2,400+',label: 'Happy Clients'},
  {icon: FaStar,value: '5M+',label: 'Sq Ft Installed'},
  {icon: FiCheckCircle,value: '100%',label: 'Satisfaction Rate'},
];

const testimonials=[
  {name: 'Marcus T.',role: 'Homeowner',quote: 'Absolutely stunning result. My garage looks better than a showroom. Worth every penny.'},
  {name: 'Brenda K.',role: 'Warehouse Manager',quote: 'Our 40,000 sq ft facility was transformed in 4 days. Incredibly professional crew.'},
  {name: 'Jason R.',role: 'Auto Dealership Owner',quote: 'The metallic finish in our showroom stopped customers in their tracks. Incredible work.'},
];

const Home=()=> {
  const fadeIn={
    initial: {opacity: 0,y: 30},
    whileInView: {opacity: 1,y: 0},
    viewport: {once: true},
    transition: {duration: 0.7},
  };

  return (
    <div className="min-h-screen bg-white">
      {/* ── HERO ── */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src="https://media-manager-c.questera.ai/greta-media/b23737eec191c3f5b765303f85ce3a4932597ae054cc2b3dadfd019ed91c748ca1e6808982e16c775001fce05a7fb0b4/images/aW1hZ2UvanBlZw==/d5e7cab1bc95d402f1d0ea2880e2ca5f.jpg" alt="Captivating Epoxy Floor" className="w-full h-full object-cover scale-105" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-br from-red-900/80 via-black/50 to-blue-900/60" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-20">
          <motion.div initial={{opacity: 0,y: 40}} animate={{opacity: 1,y: 0}} transition={{duration: 0.9,delay: 0.2}}>
            <motion.span initial={{opacity: 0,scale: 0.8}} animate={{opacity: 1,scale: 1}} transition={{delay: 0.4}} className="inline-flex items-center gap-2 py-2 px-5 rounded-full bg-white/10 border border-white/30 text-white text-sm font-bold tracking-widest mb-8 uppercase backdrop-blur-md">
              <SafeIcon icon={FaStar} className="text-yellow-400" />
              Florida's #1 Epoxy Flooring Specialists
            </motion.span>
            <h1 className="text-5xl md:text-7xl lg:text-9xl font-black text-white tracking-tight mb-6 leading-none drop-shadow-2xl">
              Your Floor.<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-red-200 to-red-500">Reimagined.</span>
            </h1>
            <p className="mt-6 text-xl md:text-2xl text-white/90 max-w-3xl mx-auto mb-12 font-medium leading-relaxed drop-shadow-lg">
              Industrial-strength epoxy systems that transform garages and warehouses into breathtaking,mirror-finish showpieces — built to last a lifetime.
            </p>
            <div className="flex flex-col sm:flex-row gap-5 justify-center mb-20">
              <Link to="/contact" className="px-10 py-5 bg-red-600 hover:bg-blue-700 text-white font-black rounded-full transition-all shadow-xl hover:shadow-blue-900/50 text-lg flex items-center justify-center gap-3 group">
                Get Your Free Estimate
                <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1.5 transition-transform" />
              </Link>
              <Link to="/gallery" className="px-10 py-5 bg-white/10 hover:bg-white/20 backdrop-blur-xl text-white font-bold rounded-full transition-all border border-white/30 hover:border-white/60 text-lg shadow-xl">
                See Our Work
              </Link>
            </div>
          </motion.div>
        </div>
        <motion.div animate={{y: [0,10,0]}} transition={{repeat: Infinity,duration: 2}} className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3 text-white">
          <span className="text-xs font-bold tracking-[0.3em] uppercase drop-shadow-md">Scroll</span>
          <div className="w-px h-10 bg-gradient-to-b from-white to-transparent" />
        </motion.div>
      </section>

      {/* ── STATS BAR ── */}
      <section className="py-12 relative z-20 shadow-lg" style={{backgroundColor: '#dc2626'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((s,i)=> (
              <motion.div key={i} initial={{opacity: 0,y: 20}} whileInView={{opacity: 1,y: 0}} viewport={{once: true}} transition={{delay: i * 0.1}} className="text-center">
                <SafeIcon icon={s.icon} className="text-red-200 text-3xl mx-auto mb-3 opacity-80" />
                <div className="text-4xl font-black text-white drop-shadow-sm">{s.value}</div>
                <div className="text-red-100 text-sm font-bold tracking-wider uppercase mt-2 opacity-90">{s.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FOCUS AREAS ── */}
      <section className="py-32 bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeIn} className="text-center mb-20">
            <span className="text-blue-600 text-sm font-black tracking-[0.2em] uppercase mb-4 block">What We Do Best</span>
            <h2 className="text-4xl md:text-6xl font-black text-gray-900 mb-6 tracking-tight">Mastered. Not Dabbled.</h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg leading-relaxed">We focus on two environments exclusively — so every job we take is executed to absolute perfection.</p>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-8">
            <motion.div {...fadeIn} className="group relative overflow-hidden rounded-[2.5rem] aspect-[4/5] cursor-pointer shadow-xl border border-gray-200">
              <img src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1600&auto=format&fit=crop&q=80" alt="Residential Garage Epoxy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-gray-900/40 to-transparent transition-opacity duration-500 group-hover:opacity-90" />
              <div className="absolute top-8 left-8">
                <span className="bg-red-600/90 backdrop-blur-md text-white text-xs font-black px-4 py-2 rounded-full uppercase tracking-widest shadow-lg">Residential</span>
              </div>
              <div className="absolute inset-0 p-10 flex flex-col justify-end translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                <h3 className="text-4xl lg:text-5xl font-black text-white mb-4">Garage Floors</h3>
                <p className="text-white/90 mb-8 leading-relaxed text-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-100">From dusty concrete to a mirror-smooth showroom finish. Hot-tire resistant,oil-proof,and jaw-droppingly beautiful.</p>
                <Link to="/services" className="inline-flex items-center gap-3 text-red-400 font-black group-hover:text-blue-300 transition-colors text-lg">
                  Explore Garage Solutions
                  <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-2 transition-transform" />
                </Link>
              </div>
            </motion.div>
            <motion.div {...fadeIn} transition={{delay: 0.15,duration: 0.7}} className="group relative overflow-hidden rounded-[2.5rem] aspect-[4/5] cursor-pointer shadow-xl border border-gray-200">
              <img src="https://media-manager-c.questera.ai/greta-media/b23737eec191c3f5b765303f85ce3a4932597ae054cc2b3dadfd019ed91c748ca1e6808982e16c775001fce05a7fb0b4/images/aW1hZ2UvanBlZw==/15ce0561618377893538a047841f7c0a.jpeg" alt="Commercial Warehouse Epoxy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-gray-900/40 to-transparent transition-opacity duration-500 group-hover:opacity-90" />
              <div className="absolute top-8 left-8">
                <span className="bg-blue-600/90 backdrop-blur-md text-white text-xs font-black px-4 py-2 rounded-full uppercase tracking-widest shadow-lg">Commercial</span>
              </div>
              <div className="absolute inset-0 p-10 flex flex-col justify-end translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                <h3 className="text-4xl lg:text-5xl font-black text-white mb-4">Warehouse Floors</h3>
                <p className="text-white/90 mb-8 leading-relaxed text-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-100">Industrial-grade coatings that endure forklifts,chemical spills,and brutal daily use — while looking absolutely flawless.</p>
                <Link to="/services" className="inline-flex items-center gap-3 text-white font-black group-hover:text-blue-300 transition-colors text-lg">
                  Explore Commercial Solutions
                  <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-2 transition-transform" />
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── THE ALL STAR DIFFERENCE ── */}
      <section className="py-32 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-24 items-center">
            <motion.div {...fadeIn}>
              <span className="text-red-600 text-sm font-black tracking-[0.2em] uppercase mb-4 block">The All Star Difference</span>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 mb-8 leading-tight tracking-tight">
                Engineered for Life.<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-blue-600">Designed to Impress.</span>
              </h2>
              <p className="text-gray-600 text-lg mb-12 leading-relaxed">We don't just apply a coating — we engineer a flooring system. Our diamond-ground surface prep and multi-layer application process creates a bond so strong,it will never peel,chip,or delaminate.</p>
              <div className="space-y-8">
                {[
                  {icon: FiShield,title: 'Extreme Durability',desc: 'Withstands abrasions,impacts,chemical spills,and heavy traffic for decades.'},
                  {icon: FiCheckCircle,title: 'Effortless Maintenance',desc: 'Seamless,non-porous surface. Mop it once and it looks brand new.'},
                  {icon: FiTrendingUp,title: 'Instant Property Value Boost',desc: 'Buyers and tenants pay a premium for properties with epoxy-coated floors.'},
                ].map((f,i)=> (
                  <motion.div key={i} initial={{opacity: 0,x: -20}} whileInView={{opacity: 1,x: 0}} viewport={{once: true}} transition={{delay: i * 0.15}} className="flex gap-6 p-6 rounded-3xl hover:bg-gray-50 transition-colors group border border-transparent hover:border-gray-100">
                    <div className="flex-shrink-0 w-16 h-16 rounded-2xl bg-red-50 group-hover:bg-blue-600 flex items-center justify-center transition-all duration-300">
                      <SafeIcon icon={f.icon} className="text-red-600 group-hover:text-white text-2xl transition-colors" />
                    </div>
                    <div>
                      <h4 className="text-xl font-bold text-gray-900 mb-2">{f.title}</h4>
                      <p className="text-gray-600 leading-relaxed">{f.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            <motion.div initial={{opacity: 0,scale: 0.92}} whileInView={{opacity: 1,scale: 1}} viewport={{once: true}} transition={{duration: 0.7}} className="relative">
              <div className="rounded-[2.5rem] overflow-hidden shadow-2xl aspect-[4/5] border-8 border-white">
                <img src="https://media-manager-c.questera.ai/greta-media/b23737eec191c3f5b765303f85ce3a4932597ae054cc2b3dadfd019ed91c748ca1e6808982e16c775001fce05a7fb0b4/images/aW1hZ2UvanBlZw==/d5e7cab1bc95d402f1d0ea2880e2ca5f.jpg" alt="Pristine Custom Epoxy Floor" className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="absolute -bottom-8 -left-8 bg-blue-900 text-white px-10 py-8 rounded-3xl shadow-xl border border-blue-800">
                <div className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-white leading-none">15+</div>
                <div className="text-blue-200 text-sm font-bold uppercase tracking-widest mt-2">Years Experience</div>
              </div>
              <div className="absolute -top-8 -right-8 bg-white p-6 rounded-3xl shadow-xl border border-gray-100 max-w-[220px] hidden lg:block">
                <div className="flex gap-1 mb-3">
                  {[...Array(5)].map((_,i)=> <SafeIcon key={i} icon={FaStar} className="text-yellow-400 text-lg" />)}
                </div>
                <p className="text-gray-900 text-sm font-bold leading-relaxed">"Best investment we ever made for our property."</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── PROCESS ── */}
      <section className="py-32 bg-gray-50 border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeIn} className="text-center mb-24">
            <span className="text-red-600 text-sm font-black tracking-[0.2em] uppercase mb-4 block">How It Works</span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 mb-6">Our Proven Process</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">Four precise steps. Zero shortcuts. A result that lasts forever.</p>
          </motion.div>
          <div className="grid md:grid-cols-4 gap-6 lg:gap-8">
            {[
              {step: '01',title: 'Free Consultation',desc: 'We assess your space,measure sq footage,and discuss your vision and budget.'},
              {step: '02',title: 'Surface Prep',desc: 'Diamond grinding removes all contamination for a perfect mechanical bond.'},
              {step: '03',title: 'Multi-Layer Application',desc: 'Base coat,broadcast flakes or metallics,then a UV-stable polyaspartic topcoat.'},
              {step: '04',title: 'Final Inspection',desc: 'We walk the floor with you. You approve it before we pack up.'},
            ].map((p,i)=> (
              <motion.div key={i} initial={{opacity: 0,y: 30}} whileInView={{opacity: 1,y: 0}} viewport={{once: true}} transition={{delay: i * 0.15}} className="relative group">
                <div className="bg-white border border-gray-100 hover:border-blue-500/50 rounded-3xl p-10 transition-all duration-300 h-full shadow-sm hover:shadow-xl">
                  <div className="text-7xl font-black text-gray-100 group-hover:text-red-100 transition-colors mb-6 leading-none tracking-tighter">{p.step}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{p.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{p.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeIn} className="text-center mb-24">
            <span className="text-blue-600 text-sm font-black tracking-[0.2em] uppercase mb-4 block">Client Reviews</span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-gray-900">What Our Clients Say</h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((t,i)=> (
              <motion.div key={i} initial={{opacity: 0,y: 30}} whileInView={{opacity: 1,y: 0}} viewport={{once: true}} transition={{delay: i * 0.15}} className="bg-gray-50 border border-gray-100 rounded-3xl p-10 shadow-lg relative">
                <SafeIcon icon={FaStar} className="absolute top-10 right-10 text-yellow-100 text-6xl" />
                <div className="flex gap-1.5 mb-8 relative z-10">
                  {[...Array(5)].map((_,j)=> <SafeIcon key={j} icon={FaStar} className="text-yellow-400 text-lg" />)}
                </div>
                <p className="text-gray-700 text-lg leading-relaxed mb-10 relative z-10 italic">"{t.quote}"</p>
                <div className="relative z-10">
                  <div className="font-black text-gray-900 text-lg">{t.name}</div>
                  <div className="text-red-600 text-sm font-bold tracking-wide uppercase mt-1">{t.role}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="relative py-40 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1616486028637-299f2b8449c4?w=2800&auto=format&fit=crop&q=80" alt="Epoxy Floor Showroom" className="w-full h-full object-cover scale-105" loading="lazy" />
          <div className="absolute inset-0 bg-red-950/90" />
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/40 to-transparent" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <motion.div {...fadeIn}>
            <h2 className="text-4xl md:text-6xl lg:text-7xl font-black text-white mb-8 tracking-tight">Ready for a Floor That Stops People in Their Tracks?</h2>
            <p className="text-red-100 text-xl md:text-2xl mb-14 max-w-2xl mx-auto font-light leading-relaxed">Get your free,no-obligation quote today. Most residential jobs are completed in just one day.</p>
            <div className="flex flex-col sm:flex-row gap-5 justify-center">
              <Link to="/contact" className="inline-flex items-center justify-center gap-3 px-10 py-5 bg-white text-red-700 font-black rounded-full transition-all shadow-xl hover:bg-gray-100 text-lg group">
                Schedule Free Consultation
                <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1.5 transition-transform" />
              </Link>
              <a href="tel:9543285964" className="inline-flex items-center justify-center gap-3 px-10 py-5 bg-transparent border-2 border-white/50 text-white font-bold rounded-full hover:bg-white/10 hover:border-white transition-all text-lg">
                Call (954) 328-5964
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;